import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2, FileText, ClipboardList } from "lucide-react";
import { api } from "../../lib/api";
import { act } from "../../lib/sales";
import { panel } from "../../lib/vi";

function Copilot({ v, ensurePid }) {
  const [d, setD] = useState(v?.copilot || null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("key");
  useEffect(() => { setD(v?.copilot || null); }, [v?.pid, v?.rid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function gen() {
    const pid = await ensurePid();
    if (!pid) return;
    setBusy(true);
    const r = await act(() => api.post(`/admin/vi/copilot/${pid}`));
    setBusy(false);
    if (r) setD(r);
  }
  const list = tab === "key" ? d?.key_points : tab === "next" ? d?.next_steps : null;
  return (
    <section data-testid="vi-copilot">
      <div className="flex items-center gap-2 mb-3"><p className="text-xs text-black/60 flex-1">Intro script, objections and next steps</p>
        <button onClick={gen} disabled={!v || busy} className="text-xs border border-indigo-300 text-indigo-700 rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40" data-testid="vi-copilot-generate">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}Generate Script</button></div>
      <div className="flex border-b border-[var(--line)] mb-3">{[["key", "Key Points"], ["obj", "Objections"], ["next", "Next Steps"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`flex-1 py-1.5 text-xs border-b-2 ${tab === k ? "border-[var(--gold)] text-[var(--ink)]" : "border-transparent text-black/65"}`} data-testid={`vi-copilot-tab-${k}`}>{l}</button>)}</div>
      <div className="h-40 overflow-y-auto" data-testid="vi-copilot-body">
        {!d && <p className="text-xs text-black/60 py-4 text-center">{v ? "Generate a personalized intro built only from this business's verified facts." : "Pick a business first."}</p>}
        {d && <div className="space-y-3 text-sm">
          {d.intro && <div className="bg-black/[0.03] rounded-xl p-3" data-testid="vi-copilot-intro"><div className="flex items-center justify-between mb-1"><p className="font-semibold">Introduction Script</p><button onClick={() => { navigator.clipboard?.writeText(d.intro); toast.success("Copied"); }} aria-label="Copy script" data-testid="vi-copilot-copy"><Copy className="w-4 h-4 text-black/60" /></button></div><p className="text-black/75 leading-relaxed">{d.intro}</p></div>}
          {tab === "key" && d.summary && <p className="text-black/70 text-xs">{d.summary}</p>}
          {list && <ul className="space-y-1.5">{list.map((x) => <li key={x} className="flex gap-2 text-black/75"><span className="text-[var(--gold)]">•</span>{x}</li>)}</ul>}
          {tab === "obj" && (d.objections || []).map((o) => <div key={o.q} className="bg-black/[0.03] rounded-lg p-2.5"><p className="text-xs font-semibold text-amber-800">“{o.q}”</p><p className="text-black/75 text-xs mt-1">{o.a}</p></div>)}
          {tab === "key" && d.offer && <p className="text-xs text-[var(--gold)]">Suggested offer: {d.offer}</p>}
        </div>}
      </div>
    </section>
  );
}

function Summary({ v, ensurePid, notes, onDraft }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({});
  useEffect(() => { setD(null); setDone({}); }, [v?.pid, v?.rid]);
  async function gen() {
    const pid = await ensurePid();
    if (!pid) return;
    setBusy(true);
    const r = await act(() => api.post(`/admin/vi/summary/${pid}`, { notes }));
    setBusy(false);
    if (r) { setD(r); onDraft?.({ subject: r.email?.subject || "", body: r.email?.body || "", sms: r.sms || "" }); }
  }
  return (
    <section data-testid="vi-summary">
      <div className="flex items-center gap-2 mb-3"><p className="text-xs text-black/60 flex-1">After the call: summary, next steps and drafts</p>
        <button onClick={gen} disabled={!v || busy} className="text-xs bg-indigo-600 text-white rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40" data-testid="vi-summary-generate">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}Generate Summary</button></div>
      <div className="h-[12.5rem] overflow-y-auto" data-testid="vi-summary-body">
        {!d && <p className="text-xs text-black/60 py-3 text-center">{v ? "After the call, generate a summary. Email and text drafts fill into Email or Text." : "Pick a business first."}</p>}
        {d?.summary && <p className="text-xs text-black/70 mb-2" data-testid="vi-summary-text">{d.summary}</p>}
        {d?.next_steps?.length > 0 && <div><p className="text-sm font-semibold mb-1">Suggested Next Steps</p>{d.next_steps.map((s) => <label key={s} className="flex items-center gap-2 text-xs py-1"><input type="checkbox" checked={!!done[s]} onChange={() => setDone({ ...done, [s]: !done[s] })} className="accent-[var(--gold)]" />{s}</label>)}
          {d.follow_up_days ? <p className="text-xs text-[var(--gold)] mt-1">Follow up in {d.follow_up_days} days</p> : null}</div>}
      </div>
    </section>
  );
}

export function AiAssistant(props) {
  const [tab, setTab] = useState("assist");
  return (
    <section className={`${panel} p-4`} data-testid="vi-ai-card">
      <div className="flex items-center gap-1 mb-3">{[["assist", "Call Assistant", FileText], ["summary", "AI Summary", ClipboardList]].map(([k, l, I]) => (
        <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-sm font-serif inline-flex items-center gap-1.5 transition-colors ${tab === k ? "bg-indigo-600 text-white" : "text-black/70 hover:bg-black/5"}`} data-testid={`vi-ai-tab-${k}`}><I className="w-4 h-4" />{l}</button>))}</div>
      <div className="h-64 overflow-hidden" data-testid="vi-ai-body">
        <div className={tab === "assist" ? "" : "hidden"}><Copilot {...props} /></div>
        <div className={tab === "summary" ? "" : "hidden"}><Summary {...props} onDraft={(d) => props.onDraft?.(d)} /></div>
      </div>
    </section>
  );
}
